import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import Accordion from 'LuxKit/Accordion'
import LargeContentToggle, { type LargeContentToggleHandle } from 'components/Common/LargeContentToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Sheet from 'components/utils/Sheet'
import { useIntl } from 'react-intl'
import { useThingsToKnowRowFocusTargets } from 'components/OfferPage/ThingsToKnow/useThingsToKnowRowFocusTargets'

const DesktopRowGrid = styled.div`
  display: grid;
  grid-template-columns: ${rem(200)} 1fr;
  align-items: start;
  gap: ${rem(24)};
  padding: ${rem(16)} 0;
`

/** Matches Accordion trigger padding for visual consistency */
const MobileStaticRow = styled(Group)`
  min-height: ${rem(48)};
  padding-block: ${rem(8)};
`

const MobileExpandedRow = styled(Group)`
  min-height: ${rem(48)};
  padding-block: ${rem(8)};
`

export type ThingsToKnowLayoutMode = 'default' | 'expanded'

export interface ThingsToKnowRow {
  key: string
  icon: React.ReactElement
  label: string
  content: React.ReactNode
  collapsable: boolean
}

interface DesktopRowContentProps {
  row: ThingsToKnowRow
  showMoreText: string
  showLessText: string
  mode: ThingsToKnowLayoutMode
  getRowRef: (key: string) => (element: HTMLElement | null) => void
  getToggleRef: (key: string) => (handle: LargeContentToggleHandle | null) => void
}

function ThingsToKnowDesktopRow({ row, showMoreText, showLessText, mode, getRowRef, getToggleRef }: DesktopRowContentProps) {
  const desktopContent = row.collapsable
    ? row.content
    : <BodyText variant="medium" colour="light-foreground-alt">{row.content}</BodyText>

  return (
    <DesktopRowGrid ref={getRowRef(row.key)}>
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {row.icon}
        <BodyText variant="medium" weight="bold" colour="normal-contrast">{row.label}</BodyText>
      </Group>
      <div>
        {mode === 'expanded' ? desktopContent : (
          <LargeContentToggle
            ref={getToggleRef(row.key)}
            maxHeight={80}
            showMoreText={showMoreText}
            showLessText={showLessText}
            toggleWeight="regular"
          >
            {desktopContent}
          </LargeContentToggle>
        )}
      </div>
    </DesktopRowGrid>
  )
}

interface MobileRowProps {
  row: ThingsToKnowRow
  isLast: boolean
  mode: ThingsToKnowLayoutMode
}

function ThingsToKnowMobileRow({ row, isLast, mode }: MobileRowProps) {
  if (mode === 'default' && row.collapsable) {
    return (
      <Accordion heading={row.label} startIcon={row.icon}>
        {row.content}
      </Accordion>
    )
  }

  if (row.collapsable) {
    return (
      <>
        <MobileExpandedRow direction="vertical" gap={8}>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {row.icon}
            <BodyText variant="medium" weight="bold" colour="normal-contrast">{row.label}</BodyText>
          </Group>
          <div>{row.content}</div>
        </MobileExpandedRow>
        {!isLast && <Divider kind="secondary" direction="horizontal" />}
      </>
    )
  }

  return (
    <>
      <MobileStaticRow direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {row.icon}
          <BodyText variant="medium" weight="bold" colour="normal-contrast">{row.label}</BodyText>
        </Group>
        <BodyText variant="medium" colour="light-foreground-alt">{row.content}</BodyText>
      </MobileStaticRow>
      {!isLast && <Divider kind="secondary" direction="horizontal" />}
    </>
  )
}

interface Props {
  rows: Array<ThingsToKnowRow>
  mode?: ThingsToKnowLayoutMode
  /** Sticky sub-nav clearance for focus-scroll -- differs slightly per offer type (see `useNavigableOfferContentScroll.ts`); defaults to the Property/Flash value. */
  scrollOffset?: number
}

function ThingsToKnowLayout({ rows, mode = 'default', scrollOffset }: Props) {
  const intl = useIntl()
  const { getRowRef, getToggleRef } = useThingsToKnowRowFocusTargets(rows, scrollOffset)

  if (!rows.length) return null

  const showMoreText = intl.formatMessage({
    id: 'R8XfLhIASfenD_Ud9Jry',
    defaultMessage: 'Read more',
  })
  const showLessText = intl.formatMessage({
    id: 'FAvqQN4xZMZay0Cz6cMB',
    defaultMessage: 'Read less',
  })

  return (
    <Sheet padding="8 0">
      <CSSBreakpoint min="tablet">
        {rows.map((row, index) => (
          <React.Fragment key={row.key}>
            <ThingsToKnowDesktopRow
              row={row}
              showMoreText={showMoreText}
              showLessText={showLessText}
              mode={mode}
              getRowRef={getRowRef}
              getToggleRef={getToggleRef}
            />
            {index < rows.length - 1 && <Divider kind="secondary" direction="horizontal" />}
          </React.Fragment>
        ))}
      </CSSBreakpoint>

      <CSSBreakpoint max="mobile">
        {rows.map((row, index) => (
          <ThingsToKnowMobileRow key={row.key} row={row} isLast={index === rows.length - 1} mode={mode} />
        ))}
      </CSSBreakpoint>
    </Sheet>
  )
}

export default ThingsToKnowLayout
